import { useAuth } from "@/store/auth";
import { useEffect, useState } from "react";
import api from "@/lib/api";

// D54-c -- session-scoped cache of the layered resolver's answer.
// Populated once per login by `useEffectivePermission()`. Also
// persisted to sessionStorage so a page reload doesn't re-fetch
// before render. Cleared on logout via auth store subscription.
let _EFFECTIVE_CACHE = null;
try {
  const stashed = typeof sessionStorage !== "undefined"
    ? sessionStorage.getItem("d54c_effective")
    : null;
  if (stashed) _EFFECTIVE_CACHE = JSON.parse(stashed);
} catch { /* ignore */ }

async function _loadEffective() {
  const { data } = await api.get("/rbac/effective");
  _EFFECTIVE_CACHE = data;
  try { sessionStorage.setItem("d54c_effective", JSON.stringify(data)); } catch { /* ignore */ }
  return data;
}

export function clearEffectiveCache() {
  _EFFECTIVE_CACHE = null;
  try { sessionStorage.removeItem("d54c_effective"); } catch { /* ignore */ }
}

/**
 * D54-c -- returns the layered permission answer for `key` with full
 * source trail. When flag is OFF on the backend, the trail still
 * resolves via the legacy role bundle so this hook can be used
 * everywhere safely (values are byte-identical to `useMatrix`).
 *
 *   const { allowed, source, trail } = useEffectivePermission("hr_leave_apply");
 *
 * The `trail` array is a 4-item list for the drawer UI:
 *   [{layer:"role",        value:...},
 *    {layer:"department",  value:null},
 *    {layer:"designation", value:...},
 *    {layer:"user",        value:...}]
 */
export function useEffectivePermission(key) {
  const { user } = useAuth();
  const [state, setState] = useState(() => _hydrateFromCache(key, user));
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) return;
      if (_EFFECTIVE_CACHE?.user_id === user.id) return;
      try {
        const fresh = await _loadEffective();
        if (!cancelled) setState(_hydrateFromCache(key, user, fresh));
      } catch { /* leave legacy fallback state */ }
    })();
    return () => { cancelled = true; };
  }, [user, key]);
  return state;
}

function _hydrateFromCache(key, user, override) {
  const cache = override || _EFFECTIVE_CACHE;
  if (!user) return { allowed: false, source: "none", trail: [] };
  if (!cache || cache.user_id !== user.id) {
    // Fallback: consult the auth store's `matrix` map (legacy path).
    // Same answer as `useMatrix()` -- so callers get correct results
    // even before the effective payload lands.
    return { allowed: false, source: "loading", trail: [] };
  }
  const cell = (cache.permissions || {})[key] || {};
  const roleVal = cell.role_value;
  const src = cell.source || "role";
  return {
    allowed: !!cell.value,
    source: src,
    trail: [
      { layer: "role",        value: roleVal ?? null },
      { layer: "department",  value: null },
      { layer: "designation", value: src === "designation" ? cell.value : null },
      { layer: "user",        value: (src === "granted" || src === "revoked") ? cell.value : null },
    ],
  };
}

export function usePermission(module, action) {
  const { user, permissions } = useAuth();
  if (!user) return false;
  // D54-d Group A -- CEO short-circuit retired. The `permissions`
  // array + role_permissions collection already grants CEO wildcard
  // access via the layered resolver + role_permissions seed. Matrix
  // + role fall-through are byte-identical for CEO on every key.
  if (!permissions || permissions.length === 0) return false;
  return permissions.some((p) => {
    const mm = p.module === module || p.module === "*";
    const aa = p.action === action || p.action === "*";
    return mm && aa;
  });
}

/** D54-d Group A -- Exposed for callers that need to distinguish
 * "not permitted" from "auth still loading". Returns `true` iff the
 * auth store has hydrated its matrix + permissions payload. Prevents
 * UI flicker on gated components after removing the CEO short-circuit.
 */
export function useMatrixLoaded() {
  const { matrix, user } = useAuth();
  return !!user && matrix !== null && matrix !== undefined;
}

/** D54-d Group A -- `useRole()` retired.
 * No callers found in the repo at retirement time. Kept as a no-op
 * stub that always returns `false` so any stray imports fail-closed
 * instead of erroring. Delete after one release cycle if grep stays
 * clean.
 */
export function useRole(..._roles) {
  return false;
}

/** D30 -- Effective RBAC matrix key check (role defaults + D26 overrides).
 * `key` is a matrix key like `hr_employees_view`, `hr_employees_edit`,
 * `hr_holidays_manage`. Prefer this over hardcoded role lists for any
 * gate that ships with a matching backend `require_permission_or_matrix`.
 *
 * Truthy on: role matrix True, "read" partial-access, active D26 grant.
 * Falsy on: no grant, active D26 revoke.
 */
export function useMatrix(key) {
  const { user, matrix } = useAuth();
  if (!user) return false;
  // D54-d Group A -- CEO short-circuit retired. Matrix cells with
  // `"CEO": True` (which is every god-view-adjacent key) resolve
  // to `true` in the auth store's `matrix` map, so the short-circuit
  // is redundant. Use `useMatrixLoaded()` if you need to distinguish
  // "false because denied" from "false because still loading".
  if (!matrix) return false;
  return !!matrix[key];
}

/** D30 -- helper for gates that pass if ANY of the keys is granted. */
export function useMatrixAny(...keys) {
  const { user, matrix } = useAuth();
  if (!user) return false;
  // D54-d Group A -- CEO short-circuit retired (same rationale as
  // `useMatrix`).
  if (!matrix) return false;
  return keys.some((k) => !!matrix[k]);
}

/** D48-c -- Vault visibility helper.
 * The sidebar Vault entry must be visible to any user with at least one
 * per-category view key (D47b introduced 19 categories with 4 gates each).
 * Uses PREFIX + SUFFIX pattern-match so newly-added `vault_*_view_own`
 * or `vault_*_view_any` keys are picked up automatically without an
 * allowlist maintenance burden.
 *
 * Codified pattern: "sidebar gates by any-of-N matrix keys, not one
 * broad module_visibility toggle" (memory/patterns.md).
 */
export function useAnyVaultAccess() {
  return useAnyModuleAccess("vault");
}

/** D48-c hotfix -- generalized any-of-N gate.
 *
 * Passes iff the user holds AT LEAST ONE matrix key of shape
 * `<module>_*_view_any` or `<module>_*_view_own`. Use for sidebar /
 * bottom-tab / hub-card visibility on any module that has moved to
 * per-category strict gates. Pattern-matches so new categories are
 * auto-included without an allowlist to maintain.
 *
 * Example:  const canSee = useAnyModuleAccess("vault");
 *           const canSeeHr = useAnyModuleAccess("hr");
 */
export function useAnyModuleAccess(modulePrefix) {
  const { user, matrix } = useAuth();
  if (!user) return false;
  // D54-d Group A -- CEO short-circuit retired. Matrix already grants
  // CEO every `*_view_any` + `*_view_own` cell; the pattern-match
  // below will match those cells naturally.
  if (!matrix) return false;
  const prefix = `${modulePrefix}_`;
  return Object.entries(matrix).some(([k, v]) => (
    v === true && k.startsWith(prefix)
      && (k.endsWith("_view_own") || k.endsWith("_view_any"))
  ));
}

/** D48-c hotfix -- per-category vault upload gate.
 *
 * Returns `{ canUpload: bool, categories: string[] }`. `canUpload`
 * is truthy iff the user holds AT LEAST ONE `vault_*_upload` key.
 * `categories` is the list of category slugs (e.g. "expense_receipts")
 * the user can actually upload to -- used to filter the category
 * dropdown in the upload dialog so an EMPLOYEE with only
 * `vault_expense_receipts_upload` sees ONLY that option.
 */
export function useVaultUploadPolicy() {
  const { user, matrix } = useAuth();
  if (!user) return { canUpload: false, categories: [] };
  // D54-d Group A -- CEO short-circuit retired. The `vault_*_upload`
  // pattern-match below matches every category CEO holds via the
  // matrix cell (all 19 categories default to CEO True), so the
  // ceremonial "canUpload: true, categories: []" fast-path is
  // redundant. Callers now get an explicit category list for CEO too.
  if (!matrix) return { canUpload: false, categories: [] };
  const cats = [];
  Object.entries(matrix).forEach(([k, v]) => {
    if (v === true && k.startsWith("vault_") && k.endsWith("_upload")) {
      // strip the `vault_` prefix and `_upload` suffix.
      cats.push(k.slice("vault_".length, -"_upload".length));
    }
  });
  return { canUpload: cats.length > 0, categories: cats };
}
